import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { Badge } from '../../common';
import { Button } from '../../common';
import { SkeletonLine } from '../../common';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { getWorkspaceStats } from '@auw/api-client';
import {
  cancelBillingSubscription,
  createBillingPortalSession,
  getBillingCatalog,
  resumeBillingSubscription,
  type BillingCatalog,
} from '@auw/api-client';
import { formatCurrencyAmount, resolveCycleAmount, resolvePlanPricing } from '@auw/api-client';
import { PLANS, formatBytes, formatTokens } from '@auw/workspace-utils';
import {
  billingCycleLabel,
  billingStatusBadgeVariant,
  billingStatusLabel,
  formatDateOnlyOrDash,
  formatDateOrDash,
  normalizePlanKey,
  openExternalInNewTab,
  parseApiError,
} from '../utils';
import { useBillingPortal } from '../hooks/useBillingPortal';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { queryKeys } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';
import {
  ActionRow,
  ConfirmActions,
  ConfirmBox,
  ConfirmDesc,
  ConfirmTitle,
  Divider,
  InlineError,
  InlineInfo,
  InlineWarning,
  PlanBox,
  PlanMeta,
  PlanName,
  PlanNameRow,
  PlanPrice,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  UsageBarFill,
  UsageBarTrack,
  UsageBlock,
  UsageLabel,
  UsageNumbers,
} from '../styles';

export const SubscriptionTab: React.FC = () => {
  const { t, i18n } = useTranslation('settings');
  const user = useUserStore((s) => s.user);
  const usage = useUserStore((s) => s.usage);
  const refreshUsage = useUserStore((s) => s.refreshUsage);
  const addToast = useChatStore((s) => s.addToast);
  /* P11-5d: SaaS-only billing affordances. On-prem tenants ship the
   * usage block but hide downgrade/customer-portal CTAs. */
  const featureFlags = useFeatureFlags();
  const customerPortalEnabled = featureFlags.isEnabled('billing.customerPortal');
  const downgradeEnabled = featureFlags.isEnabled('billing.downgradeFree');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const {
    billing,
    loading: billingLoading,
    error: billingError,
    refresh: refreshBilling,
  } = useBillingStatus({
    fallbackErrorMessage: t('subscription.loadFail'),
    initialFetch: false,
  });

  const { portalLoading, openPortal } = useBillingPortal({
    subscriptionId: billing?.subscription_id,
    addToast,
    errorTitle: t('subscription.portalFail'),
    errorFallbackMessage: t('subscription.portalFailDesc'),
  });

  const workspaceStatsQuery = useQuery({
    queryKey: queryKeys.workspace.stats('personal', null),
    queryFn: getWorkspaceStats,
    refetchOnWindowFocus: false,
  });
  const catalogQuery = useQuery<BillingCatalog>({
    queryKey: queryKeys.billing.catalog(),
    queryFn: getBillingCatalog,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const fileStorageBytes = workspaceStatsQuery.data?.total_file_size_bytes ?? 0;

  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    void refreshUsage();
    void refreshBilling();
  }, [refreshUsage, refreshBilling]);

  const handleCancelSubscription = useCallback(async () => {
    if (!billing?.subscription_id) {
      addToast({
        type: 'warning',
        title: t('subscription.noSubscription'),
        description: t('subscription.noSubscriptionDesc'),
      });
      return;
    }
    setCancelLoading(true);
    try {
      await cancelBillingSubscription(billing.subscription_id);
      setShowCancelConfirm(false);
      await refreshBilling();
      addToast({
        type: 'success',
        title: t('subscription.cancelScheduled'),
        description: t('subscription.cancelScheduledDesc'),
      });
    } catch (error) {
      const message = parseApiError(error, t('subscription.cancelFail'));
      const isPermissionError = /subscription-cancel/i.test(message) && /not authorized/i.test(message);
      if (isPermissionError) {
        try {
          const portal = await createBillingPortalSession(billing.subscription_id, 'cancel');
          addToast({
            type: 'warning',
            title: t('subscription.noInAppCancelPermission'),
            description: t('subscription.noInAppCancelPermissionDesc'),
          });
          const opened = openExternalInNewTab(portal.portal_url);
          if (!opened) {
            addToast({
              type: 'warning',
              title: t('portal.popupBlocked'),
              description: t('portal.popupBlockedDesc'),
            });
          }
          return;
        } catch {
          // portal fallback도 실패하면 기존 에러 토스트로 안내
        }
      }
      addToast({
        type: 'error',
        title: t('subscription.cancelScheduleFail'),
        description: message,
      });
    } finally {
      setCancelLoading(false);
    }
  }, [addToast, billing?.subscription_id, refreshBilling]);

  const handleResumeSubscription = useCallback(async () => {
    if (!billing?.subscription_id) {
      addToast({
        type: 'warning',
        title: t('subscription.noSubscription'),
        description: t('subscription.noSubscriptionDesc'),
      });
      return;
    }
    setResumeLoading(true);
    try {
      await resumeBillingSubscription(billing.subscription_id);
      await refreshBilling();
      addToast({
        type: 'success',
        title: t('subscription.cancelResumed'),
        description: t('subscription.cancelResumedDesc'),
      });
    } catch (error) {
      const message = parseApiError(error, t('subscription.resumeFail'));
      addToast({
        type: 'error',
        title: t('subscription.resumeFailTitle'),
        description: message,
      });
    } finally {
      setResumeLoading(false);
    }
  }, [addToast, billing?.subscription_id, refreshBilling]);

  const plan = normalizePlanKey(billing?.current_plan || user?.plan || billing?.subscription_plan) || 'free';
  const planInfo = PLANS[plan];
  const billingCatalog = catalogQuery.data ?? null;

  const tokenUsedPct = usage.tokensLimit > 0
    ? Math.min(100, Math.round((usage.tokensUsed / usage.tokensLimit) * 100))
    : 0;
  const tokenRemain = Math.max(0, usage.tokensLimit - usage.tokensUsed);
  const tokenRemainPct = 100 - tokenUsedPct;

  const actualStorageUsed = Math.max(usage.storageUsedBytes, fileStorageBytes);
  const storageUsedPct = usage.storageLimitBytes > 0
    ? Math.min(100, Math.round((actualStorageUsed / usage.storageLimitBytes) * 100))
    : 0;

  const theme = useTheme();
  const tokenBarColor = tokenRemainPct > 40 ? theme.colors.success : tokenRemainPct > 20 ? theme.colors.warning : theme.colors.error;
  const storageRemainPct = 100 - storageUsedPct;
  const storageRemain = Math.max(0, usage.storageLimitBytes - actualStorageUsed);
  const storageBarColor = storageRemainPct > 40 ? theme.colors.success : storageRemainPct > 20 ? theme.colors.warning : theme.colors.error;

  const billingStatus = billing?.billing_status || null;
  const isPaidPlan = plan !== 'free';
  const isTrialing = String(billingStatus || '').toLowerCase() === 'trialing';
  const rawScheduledPlan = String(billing?.scheduled_plan || '').trim();
  const scheduledPlanKey = normalizePlanKey(rawScheduledPlan);
  const hasScheduledPlan = Boolean(rawScheduledPlan);
  const scheduledPlanName = scheduledPlanKey
    ? PLANS[scheduledPlanKey].name
    : rawScheduledPlan.toUpperCase();
  const scheduledCycleLabel = billingCycleLabel(billing?.scheduled_cycle || billing?.billing_cycle);
  const scheduledPlanText = hasScheduledPlan
    ? `${scheduledPlanName}${scheduledCycleLabel ? ` (${scheduledCycleLabel})` : ''}`
    : t('subscription.keepCurrentPlan');
  const scheduledChangeAt = billing?.scheduled_change_effective_at || billing?.next_billed_at || null;
  const currentCycle = String(billing?.billing_cycle || '').trim().toLowerCase() === 'yearly' ? 'yearly' : 'monthly';
  const resolvedPricing = resolvePlanPricing(planInfo, billingCatalog);
  const currentCycleAmount = resolveCycleAmount(resolvedPricing, currentCycle);
  const formattedCurrentCycleAmount = formatCurrencyAmount(
    currentCycleAmount,
    resolvedPricing.currencyCode,
    i18n.language,
  );
  const planPriceText = planInfo.monthlyPrice > 0
    ? currentCycle === 'yearly'
      ? t('subscription.priceYearly', { price: formattedCurrentCycleAmount })
      : t('subscription.priceMonthly', { price: formattedCurrentCycleAmount })
    : t('subscription.free');

  return (
    <>
      {/* 플랜 정보 */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitle>{t('subscription.currentPlan')}</SectionTitle>
        </SectionHeader>
        <Divider />

        {billingLoading ? (
          <PlanBox>
            <SkeletonLine width="120px" height="20px" mb="10px" />
            <SkeletonLine width="80px" height="14px" mb="12px" />
            <SkeletonLine width="160px" height="12px" />
          </PlanBox>
        ) : (
          <PlanBox>
            <PlanNameRow>
              <PlanName>{planInfo.name}</PlanName>
              <Badge variant={billingStatusBadgeVariant(billingStatus)}>
                {billingStatusLabel(billingStatus)}
              </Badge>
            </PlanNameRow>
            <PlanPrice>
              {planPriceText}
            </PlanPrice>

            {isPaidPlan && billing?.next_billed_at && (
              <PlanMeta>{t('subscription.nextBilling', { date: formatDateOnlyOrDash(billing.next_billed_at) })}</PlanMeta>
            )}
            {isTrialing && billing?.trial_ends_at && (
              <PlanMeta>{t('subscription.trialEnds', { date: formatDateOrDash(billing.trial_ends_at) })}</PlanMeta>
            )}
          </PlanBox>
        )}
        {!!billingError && (
          <InlineError>{billingError}</InlineError>
        )}
        {hasScheduledPlan && (
          <InlineInfo>
            {t('subscription.scheduledChange', { date: formatDateOnlyOrDash(scheduledChangeAt), plan: scheduledPlanText })}
          </InlineInfo>
        )}
        {billing?.cancel_at_period_end && (
          <InlineWarning>
            {t('subscription.cancelPending', { date: formatDateOnlyOrDash(billing.current_period_end || billing.next_billed_at) })}
          </InlineWarning>
        )}
        {billing?.grace_until && (
          <InlineWarning>
            {t('subscription.gracePeriod', { date: formatDateOrDash(billing.grace_until) })}
          </InlineWarning>
        )}
        {(billing?.over_limit_tokens || billing?.over_limit_storage || billing?.over_limit_team_spaces) && (
          <InlineWarning>
            {t('subscription.overLimit')}
            {billing.over_limit_tokens ? t('subscription.overLimitTokens') : ''}
            {billing.over_limit_storage ? t('subscription.overLimitStorage') : ''}
            {billing.over_limit_team_spaces ? t('subscription.overLimitTeamSpaces') : ''}
          </InlineWarning>
        )}

        <ActionRow>
          <Button variant="primary" size="sm" onClick={() => { window.location.href = '/pricing'; }}>
            {t('subscription.changePlan')}
          </Button>
          {isPaidPlan && customerPortalEnabled && (
            <Button
              variant="ghost"
              size="sm"
              isLoading={portalLoading}
              onClick={() => { void openPortal('manage'); }}
            >
              {t('subscription.manageSubscription')}
            </Button>
          )}
        </ActionRow>
      </SectionBlock>

      {/* 사용량 */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitle>{t('subscription.usage')}</SectionTitle>
        </SectionHeader>
        <Divider />

        <UsageBlock>
          <UsageLabel>
            <span>{t('subscription.token')}</span>
            <UsageNumbers $warn={tokenRemainPct < 20}>
              {t('subscription.usageRemaining', { used: formatTokens(tokenRemain), total: formatTokens(usage.tokensLimit) })}
            </UsageNumbers>
          </UsageLabel>
          <UsageBarTrack>
            <UsageBarFill $pct={tokenRemainPct} $color={tokenBarColor} />
          </UsageBarTrack>
        </UsageBlock>

        <UsageBlock>
          <UsageLabel>
            <span>{t('subscription.storage')}</span>
            <UsageNumbers $warn={storageRemainPct < 20}>
              {t('subscription.usageRemaining', { used: formatBytes(storageRemain), total: formatBytes(usage.storageLimitBytes) })}
            </UsageNumbers>
          </UsageLabel>
          <UsageBarTrack>
            <UsageBarFill $pct={storageRemainPct} $color={storageBarColor} />
          </UsageBarTrack>
        </UsageBlock>
      </SectionBlock>

      {/* 구독 해지 (유료만) */}
      {isPaidPlan && downgradeEnabled && (
        <SectionBlock>
          <SectionHeader>
            <SectionTitle>{t('subscription.cancelSubscription')}</SectionTitle>
          </SectionHeader>
          <Divider />

          {billing?.cancel_at_period_end ? (
            <>
              <InlineInfo>
                {t('subscription.cancelScheduledInfo')}
              </InlineInfo>
              <ActionRow>
                <Button
                  variant="secondary"
                  size="sm"
                  isLoading={resumeLoading}
                  onClick={() => { void handleResumeSubscription(); }}
                >
                  {t('subscription.undoCancel')}
                </Button>
              </ActionRow>
            </>
          ) : (
            <>
              {!showCancelConfirm && (
                <ActionRow>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    {t('subscription.cancelSubscription')}
                  </Button>
                </ActionRow>
              )}
              {showCancelConfirm && (
                <ConfirmBox>
                  <ConfirmTitle>{t('subscription.cancelConfirmTitle')}</ConfirmTitle>
                  <ConfirmDesc>
                    {t('subscription.cancelConfirmDesc', { date: formatDateOnlyOrDash(billing?.current_period_end || billing?.next_billed_at) })}
                  </ConfirmDesc>
                  <ConfirmActions>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowCancelConfirm(false)}
                    >
                      {t('common:button.close')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      isLoading={cancelLoading}
                      onClick={() => { void handleCancelSubscription(); }}
                    >
                      {t('subscription.cancelAtPeriodEnd')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      isLoading={portalLoading}
                      onClick={() => { void openPortal('cancel'); }}
                    >
                      {t('subscription.cancelViaPortal')}
                    </Button>
                  </ConfirmActions>
                </ConfirmBox>
              )}
            </>
          )}
        </SectionBlock>
      )}
    </>
  );
};
